/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from "../../device/DeviceModel";
import type { TAudioTrack, TTextTrack } from "../types";

/**
 * Shape of the persisted audio track preference (language + optional codec)
 * used to restore the user's preferred audio track across playback sessions.
 */
export interface TAudioTrackPreference {
  language: string;
  codec?: string | null;
}

/**
 * Shape of the persisted closed-caption (text track) preference used to
 * restore the user's preferred track across playback sessions.
 */
export interface TTextTrackPreference {
  id: number;
  language: string;
  kind?: string | null;
}

/**
 * Relative quality ranking of audio codecs, used to pick the best available
 * track when multiple candidates share the same language/primary flag.
 */
export enum EAudioCodecRank {
  UNKNOWN = 0,
  MPEG = 1,
  AAC = 2,
  AC3 = 3,
  EC3 = 4
}

/**
 * Pure, stateless helpers for resolving and parsing audio/text track
 * preferences. These operate only on their arguments (tracks, stored
 * preference values) with no dependency on `VideoManager`/`StorageAdapter`
 * instances, keeping them easily testable in isolation.
 */
export class TrackPreferenceUtils {
  /**
   * Resolve a stored audio description track preference against the currently available
   * tracks, falling back to the best-quality description track with user prefered language
   * when no codec matches and if no such track is available then fallback to
   * the first available track
   *
   * @param tracks - Available audio tracks.
   * @param storedPreference - Previously persisted preference.
   * @returns The matching track, first available track fallback, or null.
   */
  public getAvailableAudioDescriptionTrack(
    tracks: TAudioTrack[],
    storedPreference: TAudioTrackPreference
  ): TAudioTrack | null {
    const audioDescriptionLanguageTracks: TAudioTrack[] = [];
    for (let i = 0; i < tracks.length; i += 1) {
      const track = tracks[i];
      const trackCodec =
        track.codec !== null && track.codec !== undefined ? track.codec : "";
      let isAudioDescriptionTrack: boolean = false;
      if (
        track.kind === "ad" ||
        track.roles.includes("description") ||
        track.roles.includes("describes-video") ||
        /audio description|descriptive audio|\bAD\b/i.test(track.label || "")
      ) {
        isAudioDescriptionTrack = true;
      }

      if (
        track.language === storedPreference.language &&
        isAudioDescriptionTrack
      ) {
        audioDescriptionLanguageTracks.push(track);
        if (
          storedPreference.codec === undefined ||
          storedPreference.codec === null ||
          trackCodec === storedPreference.codec
        ) {
          return track;
        }
      }
    }

    if (audioDescriptionLanguageTracks.length > 0) {
      return this.selectBestAudioTrackByCodecQuality(
        audioDescriptionLanguageTracks
      );
    }

    return null;
  }

  /**
   * Parse a raw stored value into an audio track preference, or null when
   * the value does not have the expected shape.
   *
   * @param value - Raw value read back from storage.
   * @returns The parsed preference, or null when invalid.
   */
  public parseAudioTrackPreference(
    value: unknown
  ): TAudioTrackPreference | null {
    if (
      value === null ||
      value === undefined ||
      typeof value !== "object" ||
      Array.isArray(value)
    ) {
      return null;
    }

    const recordValue = value as Record<string, string>;
    const language = recordValue["language"];
    const codec = recordValue["codec"];

    return { language, codec };
  }

  /**
   * Resolve a stored audio track preference against the currently available
   * tracks, falling back to the best-quality track with user prefered language
   * when no codec matches and if no such track is available then fallback to
   * the first available track
   *
   * @param tracks - Available audio tracks.
   * @param storedPreference - Previously persisted preference.
   * @returns The matching track, first available track fallback, or null.
   */
  public resolveTrackFromStoredPreference(
    tracks: TAudioTrack[],
    storedPreference: TAudioTrackPreference
  ): TAudioTrack | null {
    const languageTracks: TAudioTrack[] = [];
    for (let i = 0; i < tracks.length; i += 1) {
      const track = tracks[i];
      const trackCodec =
        track.codec !== null && track.codec !== undefined ? track.codec : "";
      if (track.language === storedPreference.language) {
        languageTracks.push(track);
        if (
          storedPreference.codec === undefined ||
          storedPreference.codec === null ||
          trackCodec === storedPreference.codec
        ) {
          return track;
        }
      }
    }

    if (languageTracks.length > 0) {
      return this.selectBestAudioTrackByCodecQuality(languageTracks);
    }

    return tracks[0] || null;
  }

  /**
   * Resolve the default preferred audio track when no stored preference is
   * available: prefer the device language, then a primary track, then the
   * best-quality track overall.
   *
   * @param tracks - Available audio tracks.
   * @returns The resolved default track.
   */
  public resolvePreferredAudioTrack(tracks: TAudioTrack[]): TAudioTrack {
    if (tracks.length === 1) {
      return tracks[0];
    }

    const language = DeviceModel.getDeviceLanguage();
    const languageTracks: TAudioTrack[] = [];
    for (let i = 0; i < tracks.length; i += 1) {
      if (tracks[i].language === language) {
        languageTracks.push(tracks[i]);
      }
    }
    if (languageTracks.length > 0) {
      return this.selectBestAudioTrackByCodecQuality(languageTracks);
    }

    const primaryTracks: TAudioTrack[] = [];
    for (let i = 0; i < tracks.length; i += 1) {
      if (tracks[i].primary) {
        primaryTracks.push(tracks[i]);
      }
    }
    if (primaryTracks.length > 0) {
      return this.selectBestAudioTrackByCodecQuality(primaryTracks);
    }

    return this.selectBestAudioTrackByCodecQuality(tracks);
  }

  /**
   * Pick the best track from a candidate list by codec quality rank, and
   * channel count as a tiebreaker.
   *
   * @param tracks - Candidate audio tracks (non-empty).
   * @returns The best-ranked track.
   */
  private selectBestAudioTrackByCodecQuality(
    tracks: TAudioTrack[]
  ): TAudioTrack {
    let bestTrack: TAudioTrack = tracks[0];
    let bestRank: EAudioCodecRank = this.getAudioCodecRank(bestTrack.codec);
    let bestChannels: number =
      bestTrack.channelsCount !== null && bestTrack.channelsCount !== undefined
        ? bestTrack.channelsCount
        : 0;

    for (let i = 1; i < tracks.length; i += 1) {
      const track = tracks[i];
      const rank = this.getAudioCodecRank(track.codec);
      const channels =
        track.channelsCount !== null && track.channelsCount !== undefined
          ? track.channelsCount
          : 0;
      if (rank > bestRank || (rank === bestRank && channels > bestChannels)) {
        bestTrack = track;
        bestRank = rank;
        bestChannels = channels;
      }
    }

    return bestTrack;
  }

  /**
   * Rank an audio codec string by relative quality.
   *
   * @param codec - The audio codec string (may be null).
   * @returns The codec's quality rank.
   */
  public getAudioCodecRank(codec: string | null): EAudioCodecRank {
    if (codec === null || codec === undefined || codec.length === 0) {
      return EAudioCodecRank.UNKNOWN;
    }

    const codecValue = codec.toLowerCase();

    if (
      codecValue.includes("ec-3") ||
      codecValue.includes("eac3") ||
      codecValue.includes("e-ac-3") ||
      codecValue.includes("audio/eac3")
    ) {
      return EAudioCodecRank.EC3;
    }

    if (codecValue.includes("ac-3") || codecValue.includes("ac3")) {
      return EAudioCodecRank.AC3;
    }

    if (
      codecValue.includes("mp4a") ||
      codecValue.includes("aac") ||
      codecValue.includes("audio/mp4")
    ) {
      return EAudioCodecRank.AAC;
    }

    if (codecValue.includes("mpeg")) {
      return EAudioCodecRank.MPEG;
    }

    return EAudioCodecRank.UNKNOWN;
  }

  /**
   * Parse a raw stored value into a text track preference, or null when the
   * value does not have the expected shape.
   *
   * @param value - Raw value read back from storage.
   * @returns The parsed preference, or null when invalid.
   */
  public parseTextTrackPreference(value: unknown): TTextTrackPreference | null {
    if (
      value === null ||
      value === undefined ||
      typeof value !== "object" ||
      Array.isArray(value)
    ) {
      return null;
    }

    const recordValue = value as Record<string, unknown>;
    const idValue = recordValue["id"];
    const id = typeof idValue === "number" ? idValue : -1;
    const language =
      typeof recordValue["language"] === "string"
        ? recordValue["language"]
        : "";
    const kindValue = recordValue["kind"];
    const kind = typeof kindValue === "string" ? kindValue : null;

    return { id, language, kind };
  }

  /**
   * Resolve a stored text track preference against the currently available
   * tracks. Track ids are stream-scoped, so a stored id is only trusted when
   * the track it points to still carries the stored language; otherwise the
   * language (+ kind) fallback restores the preferred language.
   *
   * @param tracks - Available text tracks.
   * @param storedPreference - Previously persisted preference.
   * @returns The matching track, or null when no match is found.
   */
  public resolveTrackFromStoredTextPreference(
    tracks: TTextTrack[],
    storedPreference: TTextTrackPreference
  ): TTextTrack | null {
    const hasStoredLanguage: boolean = storedPreference.language.length > 0;
    for (let i = 0; i < tracks.length; i += 1) {
      if (
        tracks[i].id === storedPreference.id &&
        (!hasStoredLanguage || tracks[i].language === storedPreference.language)
      ) {
        return tracks[i];
      }
    }

    for (let i = 0; i < tracks.length; i += 1) {
      const track = tracks[i];
      const trackKind =
        track.kind !== null && track.kind !== undefined ? track.kind : "";
      if (track.language === storedPreference.language) {
        if (
          storedPreference.kind === undefined ||
          storedPreference.kind === null ||
          trackKind === storedPreference.kind
        ) {
          return track;
        }
      }
    }

    return null;
  }

  /**
   * Resolve the default preferred text track when no stored preference is
   * available: prefer the device language, then a primary track, then the
   * first available track.
   *
   * @param tracks - Available text tracks.
   * @returns The resolved default track.
   */
  public resolvePreferredTextTrack(tracks: TTextTrack[]): TTextTrack {
    if (tracks.length === 1) {
      return tracks[0];
    }

    const language = DeviceModel.getDeviceLanguage();
    for (let i = 0; i < tracks.length; i += 1) {
      if (tracks[i].language === language) {
        return tracks[i];
      }
    }

    for (let i = 0; i < tracks.length; i += 1) {
      if (tracks[i].primary) {
        return tracks[i];
      }
    }

    return tracks[0];
  }
}
